import React, { useMemo, useState } from 'react';
import { Badge, Spinner, Table } from 'react-bootstrap';
import { ProviderHistoryEntry, ProviderHistoryResponse, ProviderHistoryStatus } from './types';

// ─── Константы ────────────────────────────────────────────────────────────────

const PROVIDER_BADGE: Record<string, string> = {
  marble:  'primary',
  crystal: 'info',
};

const STATUS_BADGE: Record<ProviderHistoryStatus, string> = {
  passed:       'success',
  failed:       'danger',
  inconclusive: 'warning',
  error:        'secondary',
};

const STATUS_LABELS: Record<ProviderHistoryStatus, string> = {
  passed:       'Пройдено',
  failed:       'Не пройдено',
  inconclusive: 'Неоднозначно',
  error:        'Ошибка',
};

// Приоритет статусов для вычисления итогового статуса группы
const STATUS_PRIORITY: Record<ProviderHistoryStatus, number> = {
  error:        3,
  failed:       2,
  inconclusive: 1,
  passed:       0,
};

// ─── Типы группировки ─────────────────────────────────────────────────────────

interface ProviderGroup {
  groupKey: string;                // refId или id первой записи
  refId?: string;
  occurredAt: string;              // самая поздняя дата в группе
  items: ProviderHistoryEntry[];
  overallStatus: ProviderHistoryStatus;
}

// ─── Вспомогательные функции ─────────────────────────────────────────────────

function groupItems(items: ProviderHistoryEntry[]): ProviderGroup[] {
  const map = new Map<string, ProviderHistoryEntry[]>();

  for (const item of items) {
    const key = item.refId || item.id;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(item);
  }

  return Array.from(map.entries()).map(([key, groupItems]) => {
    const sorted = [...groupItems].sort(
      (a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime(),
    );

    const overallStatus = sorted.reduce<ProviderHistoryStatus>((worst, item) => {
      return STATUS_PRIORITY[item.status] > STATUS_PRIORITY[worst] ? item.status : worst;
    }, 'passed');

    return {
      groupKey: key,
      refId: sorted[0].refId,
      occurredAt: sorted[0].occurredAt,
      items: sorted,
      overallStatus,
    };
  });
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString('ru-RU');
}

function shortId(id?: string): string {
  if (!id) return '—';
  return id.length > 16 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}

// ─── JSON-просмотрщик ─────────────────────────────────────────────────────────

const PayloadViewer: React.FC<{ payload: unknown }> = ({ payload }) => {
  const text = useMemo(() => {
    if (payload === null || payload === undefined) return '(нет данных)';
    try { return JSON.stringify(payload, null, 2); }
    catch { return String(payload); }
  }, [payload]);

  return (
    <pre className="small bg-light p-2 rounded mb-0" style={{ maxHeight: 200, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
      {text}
    </pre>
  );
};

// ─── Строка отдельной проверки провайдера ─────────────────────────────────────

const CheckRow: React.FC<{ entry: ProviderHistoryEntry }> = ({ entry }) => {
  const [expanded, setExpanded] = useState(false);
  const statusBg  = STATUS_BADGE[entry.status] ?? 'secondary';
  const statusLabel = STATUS_LABELS[entry.status] ?? entry.status;
  const providerBg = PROVIDER_BADGE[entry.source] ?? 'secondary';

  return (
    <>
      <tr
        onClick={() => setExpanded(e => !e)}
        style={{ cursor: 'pointer', background: '#fafafa' }}
      >
        <td style={{ paddingLeft: 32 }}>
          <Badge bg={providerBg} style={{ fontSize: '0.7rem' }}>
            {entry.source === 'marble' ? 'Marble' : entry.source === 'crystal' ? 'Crystal' : entry.source}
          </Badge>
        </td>
        <td className="small text-muted">{formatDate(entry.occurredAt)}</td>
        <td>
          <Badge bg={statusBg} style={{ fontSize: '0.7rem' }}>{statusLabel}</Badge>
        </td>
        <td className="small text-truncate" style={{ maxWidth: 280 }}>
          {entry.summary || '—'}
        </td>
        <td className="text-center small text-muted">
          {entry.score != null && entry.score > 0 ? entry.score : '—'}
        </td>
        <td className="text-center small text-muted">{expanded ? '▲' : '▼'}</td>
      </tr>
      {expanded && (
        <tr style={{ background: '#f5f5f5' }}>
          <td colSpan={6} className="px-3 py-2">
            <PayloadViewer payload={entry.payload} />
          </td>
        </tr>
      )}
    </>
  );
};

// ─── Строка группы (транзакция / запрос скоринга) ─────────────────────────────

const GroupRow: React.FC<{ group: ProviderGroup }> = ({ group }) => {
  const [expanded, setExpanded] = useState(true);

  const statusBg    = STATUS_BADGE[group.overallStatus] ?? 'secondary';
  const statusLabel = STATUS_LABELS[group.overallStatus] ?? group.overallStatus;

  const providerTags = Array.from(new Set(group.items.map(i => i.source)));

  return (
    <>
      {/* Заголовок группы — ID запроса скоринга */}
      <tr
        onClick={() => setExpanded(e => !e)}
        style={{ cursor: 'pointer', background: '#fff' }}
        className="fw-semibold"
      >
        <td colSpan={3}>
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted small">{expanded ? '▼' : '▶'}</span>
            <code style={{ fontSize: '0.78rem' }}>{shortId(group.refId)}</code>
            {providerTags.map(src => (
              <Badge
                key={src}
                bg={PROVIDER_BADGE[src] ?? 'secondary'}
                style={{ fontSize: '0.65rem' }}
              >
                {src === 'marble' ? 'Marble' : src === 'crystal' ? 'Crystal' : src}
              </Badge>
            ))}
          </div>
        </td>
        <td className="small text-muted">{formatDate(group.occurredAt)}</td>
        <td>
          <Badge bg={statusBg} style={{ fontSize: '0.7rem' }}>{statusLabel}</Badge>
        </td>
        <td className="text-center small text-muted">{group.items.length} проверок</td>
      </tr>

      {/* Строки проверок */}
      {expanded && group.items.map(entry => (
        <CheckRow key={entry.id} entry={entry} />
      ))}
    </>
  );
};

// ─── Основной компонент ───────────────────────────────────────────────────────

interface Props {
  loading: boolean;
  data: ProviderHistoryResponse | null;
  error?: string;
}

const AmlProviderHistory: React.FC<Props> = ({ loading, data, error }) => {
  const groups = useMemo(() => {
    if (!data?.items?.length) return [];
    return groupItems(data.items);
  }, [data]);

  // AML-17: явно подсветить ситуацию, когда один из провайдеров не выполнял
  // проверок. Раньше при отсутствии Crystal'а просто не было секции — оператор
  // не понимал, "не вызывался" или "нет данных". Теперь explicit info-блок.
  const providerCounts = useMemo(() => {
    const items = data?.items ?? [];
    return {
      marble: items.filter(i => i.source === 'marble').length,
      crystal: items.filter(i => i.source === 'crystal').length,
    };
  }, [data]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" size="sm" className="me-2" />
        <span className="text-muted small">Загрузка истории провайдеров…</span>
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-danger py-2 small">{error}</div>;
  }

  if (!data || data.items.length === 0) {
    return (
      <div className="text-center py-5 text-muted">
        <div className="fs-4 mb-2">📋</div>
        <div className="small">История проверок от провайдеров отсутствует.</div>
        <div className="small mt-1 text-secondary">
          Marble и Crystal ещё не проверяли данный запрос.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="small text-muted">
          Запросов: {groups.length} · Проверок: {data.total}
        </span>
      </div>
      <Table hover responsive size="sm" className="align-middle mb-0">
        <thead className="table-light">
          <tr>
            <th colSpan={3}>ID запроса / Провайдеры</th>
            <th>Дата</th>
            <th>Статус</th>
            <th className="text-center">Кол-во</th>
          </tr>
        </thead>
        <tbody>
          {groups.map(group => (
            <GroupRow key={group.groupKey} group={group} />
          ))}
        </tbody>
      </Table>

      {/* AML-17: предупреждение если одного провайдера нет в данных */}
      {providerCounts.crystal === 0 && providerCounts.marble > 0 && (
        <div className="alert alert-warning py-2 mt-3 small mb-0">
          <strong>Crystal:</strong> KYT-проверка не выполнялась для этого запроса.
          Возможные причины: истекла лицензия Crystal API, scoring-сервис
          пропустил Crystal-критерий (short-circuit после Marble decline), или
          операция была non-blockchain (Crystal не применим). Marble выполнил
          {' '}{providerCounts.marble} проверк{providerCounts.marble === 1 ? 'у' : 'и'}.
        </div>
      )}
      {providerCounts.marble === 0 && providerCounts.crystal > 0 && (
        <div className="alert alert-warning py-2 mt-3 small mb-0">
          <strong>Marble:</strong> rule-engine не выполнял проверку для этого
          запроса. Crystal выполнил {providerCounts.crystal} KYT-проверк{providerCounts.crystal === 1 ? 'у' : 'и'}.
        </div>
      )}
    </div>
  );
};

export default AmlProviderHistory;
